import type { ToneNotation } from "@kensio/pinyinjs";

import { askHeldWords } from "../site/save-word-route.js";
import { hasSession, startSaveWords } from "../site/save-word.js";
import type { SegmentedWord } from "../segmenting/segmented-words.js";
import type { SegmenterSaving } from "./segmenter-saving.js";
import type { SegmenterView } from "./segmenter-view.js";
import type { WordOrder } from "../segmenting/word-order.js";

/** Saved-word state for the rows in the current segmentation. */
export class SegmenterSavedWords {
  readonly #view: SegmenterView;
  readonly #saving: SegmenterSaving;
  readonly #redraw: () => void;

  #held: Set<string> | undefined;
  #read = 0;

  constructor(
    view: SegmenterView,
    saving: SegmenterSaving,
    redraw: () => void,
  ) {
    this.#view = view;
    this.#saving = saving;
    this.#redraw = redraw;
  }

  private reset(): void {
    this.#read += 1;
    this.#held = undefined;
  }

  /** Draw and wire the rows against the saved state already known. */
  show(
    words: readonly SegmentedWord[],
    order: WordOrder,
    notation: ToneNotation,
  ): void {
    this.#view.show(words, order, notation, this.#held);
    this.wire();
  }

  /** Wire the controls created by the latest draw. */
  private wire(): void {
    startSaveWords(document, globalThis.fetch, {
      ask: false,
      tag: () => this.#saving.tag(),
      valid: ({ held }) => held || this.#saving.validTag(),
      onChange: ({ word, held }) => this.#change(word, held),
      onFull: () => this.#saving.showFullList(),
    });
  }

  #change(word: string, held: boolean): void {
    this.#read += 1;
    const known = new Set(this.#held ?? []);

    if (held) {
      known.add(word);
    } else {
      known.delete(word);
    }

    this.#held = known;
    this.#saving.clearStatus();
    this.#redraw();
  }

  /** Read only the dictionary headwords in the current result. */
  async read(words: readonly SegmentedWord[]): Promise<void> {
    this.reset();
    const read = ++this.#read;

    if (!hasSession(document)) {
      return;
    }

    const asked = words.flatMap((word) =>
      word.headword === undefined ? [] : [word.headword],
    );
    const held = await askHeldWords(globalThis.fetch, asked).catch(
      () => undefined,
    );

    if (read === this.#read && held !== undefined) {
      this.#held = new Set(held);
      this.#redraw();
    }
  }
}
